import {Link, useNavigate} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch} from "@fortawesome/free-solid-svg-icons";
import {getCsrfToken, isAdmin, setUserInfo} from "../App";
import {FoodEntry} from "../domain/FoodEntry";

/**
 * A map containing food entries being used in this recipe
 */
let foodCache: { [key: string]: FoodEntry } = {};

export function resetFoodCache() {
    foodCache = {};
}

/**
 * return the food entry from the cache if it exists.
 * @param foodId the nutri id
 * @returns {FoodEntry}
 */
export function getFoodCacheEntry(foodId: number | string) {
    return foodCache["nutri" + foodId];
}

/**
 * @callback recipesLoadedCb
 * @return {boolean} if the callback loaded data
 */

/**
 * @param rcp {RcpRecipe} the recipe
 * @param onSuccess {recipesLoadedCb} called when the
 */
export function readAllFoodEntries(rcp: any, loadedCb: (success: boolean) => void) {
    if (rcp == null) return;
    let allFoodEntry: number[] = [];
    rcp.ingredientSets.forEach((is: any) => {
        is.ingredients.forEach((ing: any) => allFoodEntry.push(ing.foodEntryId));
    });
    return getFoodEntriesUsingList(allFoodEntry, loadedCb);
}

/**
 * Loads all the food entries from the combined food entry into the cache
 * @param combinedFoodEntry {CombinedFoodEntry} combined food entry to loaded items from
 * @param loadedCb {recipesLoadedCb} called when the data is loaded
 */
export function readAllFoodEntriesFromCombined(combinedFoodEntry: any, loadedCb: (success: boolean) => void) {
    if (combinedFoodEntry == null) return;
    let allFoodEntry: number[] = [];
    combinedFoodEntry.ingredients.forEach((ing: any) => allFoodEntry.push(ing.ingredientEntryId));
    return getFoodEntriesUsingList(allFoodEntry, loadedCb);

}

export function getFoodEntriesUsingList(allFoodEntries: number[], loadedCb: (success: boolean) => void) {
    if(allFoodEntries.length === 0) {
        loadedCb(true);
        return;
    }
    let allFoodsCombined = allFoodEntries.join(",");

    fetch(`/api/v1/foodEntry/idList/${allFoodsCombined}`, {
        method: "GET",
        headers: {"Content-Type": "application/json", "Accept": "application/json"},
    }).then((response) => {
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json(); // Parse JSON response
    }).then((jsonData: any[]) => {
        foodCache = {};
        jsonData.forEach(fe => foodCache["nutri" + fe.foodEntryId] = FoodEntry.fromJson(fe));
        loadedCb(true);
    });
}

export function FoodSearchPopup({value, onFoodSelected}: { value?: any, onFoodSelected: (food: FoodEntry) => void }) {
    let navigate = useNavigate();

    const [visible, setVisible] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [currentSearchStr, setCurrentSearchStr] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    if(!visible) {
        return <button onClick={e=> setVisible(!visible)}><FontAwesomeIcon icon={faSearch}/></button>
    }

    function foodEntryClicked(foodData: any) {
        let nutriId = foodData.id;
        setVisible(false);
        fetch(`/api/v1/foodEntry/id/${nutriId}`, {
            method: "GET", headers: {"Content-Type": "application/json", "Accept": "application/json"},
        }).then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
        }).then((jsonData) => {
            const foodEntry = FoodEntry.fromJson(jsonData);
            foodCache["nutri" + nutriId] = foodEntry;
            onFoodSelected(foodEntry);
        })
    }

    function validateAndSearch(event: React.MouseEvent) {
        if(searchParam?.length !== 0) {
            setCurrentSearchStr(searchParam);
        }
        event.preventDefault();
    }

    return <div className="foodSearchPopup">
        <span onClick={e => setVisible(false)} className="close">X</span>
        <h2>Search food database</h2>
        <input type="text" placeholder="Search for food..." onChange={e=> setSearchParam(e.target.value)}
               value={searchParam} />
        <button onClick={e => validateAndSearch(e)}>Search</button>
        <DataTable searchParam={currentSearchStr} mode="search" limit={25} actionOnClick={foodEntryClicked}/>
    </div>
}

/**
 * The callback takes an abbreviated food entry and takes whatever action is needed in the context.
 * @callback searchActionCb
 * @param {id,category,name}
 */

/**
 * A card based view of search results, works only with abbreviated nutrient entry elements.
 *
 * @param searchParam the search parameter string, normally a text field state
 * @param mode the mode of the search, one of search, category, userName
 * @param limit the maximum results to return
 * @param actionOnClick {searchActionCb} the action callback function
 * @return {JSX.Element}
 * @constructor
 */
export function DataTable({ searchParam, mode, limit, actionOnClick = null }: { searchParam: string, mode: string, limit: number, actionOnClick?: ((item: any) => void) | null }) {
    const [data, setData] = useState<any[]>([]); // State to store the fetched data
    const [loading, setLoading] = useState(true); // To show a "loading" state
    const [error, setError] = useState<string | null>(null); // To handle and display errors
    const navigate = useNavigate();

    // Fetch data from an API
    useEffect(() => {
        if(!searchParam || searchParam.length === 0) {
            setLoading(false);
            setData([]);
        }
        let response;
        if (mode === 'category') {
            response = fetch(
                `/api/v1/foodEntry/byCategory/${encodeURIComponent(searchParam ?? "")}?l=${limit}`, {
                    method: "GET",
                    headers: {"Content-Type": "application/json", "Accept": "application/json"},
                });
        } else if(mode === 'userName') {
            response = fetch(
                `/api/v1/foodEntry/byUser/${encodeURIComponent(searchParam ?? "")}?l=${limit}`, {
                    method: "GET",
                    headers: {"Content-Type": "application/json", "Accept": "application/json"},
                }
            )
        } else {
            response = fetch(
                `/api/v1/foodEntry/textSearch?s=${encodeURIComponent(searchParam ?? "")}&l=${limit}`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json", "Accept": "application/json"},
                });
        }

        response.then((response) => {
            if (response.status === 401 || response.status === 403) {
                setError("Unauthorized");
                return;
            } else if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json(); // Parse JSON response
        })
            .then((jsonData) => {
                setData(jsonData); // Set data into state
                setLoading(false); // Turn off loading spinner
            })
            .catch((err) => {
                setError(err.message); // Handle errors
                setLoading(false);
            });
    }, [searchParam]);

    // If still loading, show a spinner or text
    if (loading) {
        return <p>Loading your results...</p>;
    }

    // Display the error message if there was one
    if (error === "Unauthorized") {
        navigate("/login");
        return <p>Unauthorized</p>;
    } else if (error) {
        return <p>Error: {error}</p>;
    }

    return (
        <div>
            {data.length === 0 ? (
                <p>No search results</p>
            ) : (
                <div className="foodCardContainer">
                    {data.map((item) => (
                        <div key={item.id} className="foodCard">
                            <h3>
                                { actionOnClick == null ? <Link to={`/foodView?id=${item.id}`}>{item.name}</Link> : <button className="link-button" onClick={() => actionOnClick(item)}>{item.name}</button>}
                            </h3>
                            <p>
                                Category:&nbsp;
                                {isAdmin()?<Link to={`/foodSearch?mode=category&searchParam=${item.category}`}>{item.category}</Link>:<span>{item.category}</span>}
                            </p>
                            <p>Source: {item.foodSourceName}<span style={{float:"right"}}>ID: {item.id}</span></p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


/**
 * Calculate the value based on an effective portion size which is the multiplier of 100g. Eg 10g would be 0.1.
 * @param nutrient {Nutrient} the nutrient
 * @param effectivePortion {number} the effective portion size
 * @param nutrients {[FoodNutrient]}
 * @return {string}
 */
export function calculateNutrient(nutrient: any, effectivePortion: number, nutrients: any[]) {
    let foundNut = nutrients.find(n => n.nutrient === nutrient);
    if(!foundNut) return "N/A";
    const val = (foundNut.value * effectivePortion);
    return (val / 100.0).toFixed(2) + nutrient.defMeasure.unit;
}